import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {useHistory} from "react-router-dom";
import {SetupInterface} from "../../../interfaces/private/SetupInterface";
import SetupApi from "../../../common/api/functions/Setup";
import {ResponseInterface} from "../../../interfaces/ResponseInterface";
import {config} from "../../../config";
import QLoading from "../../../common/components/QLoading";
import QButton from "../../../common/components/QButton";
import QModal from "../../../common/components/QModal";
import BodyStripeConnectComponent from "./components/BodyStripeConnectComponent";
import {maskStripeAccessToken, redirectToStripeOAuth} from "../../../common/helpers/stripeOAuth";
import {setToast} from "../../../app/rootReducer";
import {QHelpers} from "../../../common/helpers";
import SettingsPageHeader from "../Settings/components/SettingsPageHeader";
import SettingsPanelCard from "../Settings/components/SettingsPanelCard";
import {getSyncEngineVersion} from "../../../common/helpers/syncEngine";

const Stripe = () => {
    const { t } = useTranslation();
    const isV2 = getSyncEngineVersion() === "v2";
    const dispatch = useDispatch();
    const history = useHistory();
    const [setup, setSetup] = useState<SetupInterface>({});
    const [loading, setLoading] = useState(true);
    const [openManualModal, setOpenManualModal] = useState(false);
    const objectParams = QHelpers.object.convertParamUrlToObject(window.location.search);

    useEffect(() => {
        if (loading) {
            load().then(() => {});
        }
    }, [loading]);

    useEffect(() => {
        if (objectParams?.connected === "1") {
            dispatch(setToast({
                message: t("stripe.oauthSuccess"),
                type: config.TOAST_TYPE.SUCCESS,
            }));
            history.replace(window.location.pathname);
        } else if (objectParams?.error === "stripe_config") {
            dispatch(setToast({
                message: t("stripe.oauthConfigError"),
                type: config.TOAST_TYPE.ERROR,
            }));
            history.replace(window.location.pathname);
        }
    }, [objectParams?.connected, objectParams?.error, dispatch, history, t]);

    const load = async () => {
        try {
            await SetupApi.detail().then(async (response: ResponseInterface | any) => {
                if (response?.status === config.STATUS_CODE.OK) {
                    setSetup(response?.data?.data || {});
                }
                await setLoading(false);
            });
        } catch (err) {
            await setLoading(false);
        }
    };

    const handleOAuthReconnect = () => {
        const started = redirectToStripeOAuth("stripe");
        if (!started) {
            dispatch(setToast({
                message: t("stripe.oauthMissingToken"),
                type: config.TOAST_TYPE.ERROR,
            }));
        }
    };

    const isConnected = Boolean(setup.stripe_access_token);

    return (
        <React.Fragment>
            <QModal
                open={openManualModal}
                setOpen={setOpenManualModal}
                headerContent={t("stripe.manualUpdateTitle")}
                bodyContent={(
                    <BodyStripeConnectComponent
                        setOpen={setOpenManualModal}
                        setLoadingFunc={setLoading}
                        onSuccess={load}
                    />
                )}
            />
            <QLoading loading={loading} />
            <div className="row">
                <SettingsPageHeader
                    title={t("stripe.pageTitle")}
                    showBackToDashboard={isV2}
                    actions={(
                        <>
                            <QButton
                                type="primary"
                                onClick={handleOAuthReconnect}
                                content={isConnected ? t("stripe.reconnectOAuth") : t("stripe.connectOAuth")}
                            />
                            <QButton
                                type="default"
                                onClick={() => setOpenManualModal(true)}
                                content={t("stripe.manualUpdate")}
                            />
                        </>
                    )}
                />
                <div className="col-lg-12">
                    <SettingsPanelCard
                        title={setup.stripe_business_name ?? t("stripe.notConnected")}
                        badge={{
                            label: isConnected ? t("stripe.connected") : t("stripe.notConnected"),
                            variant: isConnected ? "success" : "secondary",
                        }}
                        help={!isConnected ? t("stripe.notConnectedHelp") : undefined}
                        fields={[
                            {label: t("stripe.accountId"), value: setup.stripe_user_id || t("stripe.accountIdManual")},
                            ...(isConnected ? [{
                                label: t("stripe.accessTokenHint"),
                                value: (
                                    <>
                                        <code className="text-muted">{maskStripeAccessToken(setup.stripe_access_token)}</code>
                                        <span className="text-muted small ms-2">({t("stripe.accessTokenHintHelp")})</span>
                                    </>
                                ),
                                fullWidth: true,
                            }] : []),
                            {
                                label: t("stripe.address"),
                                value: setup.stripe_business_support_address,
                                fullWidth: true,
                            },
                            {label: t("stripe.email"), value: setup.stripe_business_support_email},
                            {label: t("stripe.url"), value: setup.stripe_business_support_url},
                            {label: t("stripe.phone"), value: setup.stripe_business_support_phone},
                        ]}
                    />
                </div>
            </div>
        </React.Fragment>
    );
};

export default React.memo(Stripe);
